import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { regionalLanguages } from '../../../features/upload/uploadSlice';
import { marked } from 'marked';
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import PanchayatiRajLogo from '../../../assets/images/PanchayatiRajLogo.png';
import { useSelector } from 'react-redux';
import html2pdf from "html2pdf.js";
import protectedAPI from '../../../services/axiosInstance';
import { disclaimerTranslations, en_translate_language_map } from '../../../constants/disclaimerTranslations';

const FaDownload = require('react-icons/fa').FaDownload;
const FaListAlt = require('react-icons/fa').FaListAlt;
const FaRegCopy = require('react-icons/fa').FaRegCopy;
const FaCaretDown = require('react-icons/fa').FaCaretDown;
const FaPencilAlt = require('react-icons/fa').FaPencilAlt;


interface TranslateSummaryProps {
    content: any;
    language: any;
    htmlSummaryReg: string;
    setHtmlSummaryReg: React.Dispatch<React.SetStateAction<string>>;
}


const TranslateSummary: React.FC<TranslateSummaryProps> = ({
    content,
    language,
    htmlSummaryReg,
    setHtmlSummaryReg,
}) => {
    const printRef = useRef<HTMLDivElement>(null);
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const [selectedLan, setSelectedLang] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(true);
    const file = useSelector((state: any) => state.upload.file);
    const meetingAttendees = useSelector((state: any) => state.upload.meetingAttendees);
    const task_id = useSelector((state: any) => state.upload.apiInfo?.task_id);

    const [storageData, setStorageData] = useState<any>(() => {
        const saved = localStorage.getItem('loginDetails');
        try {
            return saved ? JSON.parse(saved) : {};
        } catch (e) {
            console.error('Failed to parse loginDetails from localStorage:', e);
            return {};
        }
    });

   

    useEffect(() => {
        const selectedLanguage = regionalLanguages.find((item: any) => item.code === language);
        setSelectedLang(selectedLanguage?.label || '');
    }, [language]);

    useEffect(() => {
        if (content?.translated_summary) {
            const cleaned = content.translated_summary
                .replace(/\*\*\s*<div/gi, "<div><div class='font-bold'")
                .replace(/<\/div>\s*\*\*/gi, "</div></div>");
            const html: any = marked.parse(cleaned);
            setHtmlSummaryReg(html);
        }
    }, [content?.translated_summary]);


    const handleCopy = (htmlString: string) => {
        const tempElement = document.createElement('div');
        tempElement.innerHTML = htmlString;
        const plainText = tempElement.textContent || tempElement.innerText || '';

        navigator?.clipboard?.writeText(plainText)
            .then(() => toast.success('Copied to clipboard!'))
            .catch(() => toast.error('Failed to copy!'));
    };


    const handleExportPDF = (
        htmlContent: string,
        lang: string = "english",
        fileName: string = "Minutes_of_Meeting.pdf"
    ) => {
        if (!htmlContent) {
            toast.error("No content to export.");
            return;
        }

        const {
            state_name_english,
            zpname,
            zpcode,
            bpname,
            bpcode,
            gpname,
            gpcode,
        } = storageData || {};

        const meetingType = file?.meetingTypeName || "-";
        const meetingTitle = file?.meetingTitle || "-";

        /* -------- LANGUAGE DETECTION -------- */

        let langCode = "en";

        if (lang && lang.toLowerCase() !== "english") {
            const foundLang = Object.entries(en_translate_language_map).find(
                ([, name]) => name.toLowerCase() === lang.toLowerCase()
            );
            langCode = foundLang ? foundLang[0] : "en";
        }

        const englishDisclaimer = disclaimerTranslations["en"];
        const regionalDisclaimer = disclaimerTranslations[langCode];

        /* -------- DISCLAIMER -------- */

        let disclaimerHTML = "";

        if (langCode === "en") {
            disclaimerHTML = `
      <div class="pdf-block disclaimer">
        <p>
          <b class="text-danger">${englishDisclaimer.title}:</b>
          ${englishDisclaimer.text}
        </p>
      </div>
    `;
        } else {
            disclaimerHTML = `
      <div class="pdf-block disclaimer">
        <p>
          <b class="text-danger">${regionalDisclaimer?.title}:</b>
          ${regionalDisclaimer?.text}
        </p>
        <p>
          <b class="text-danger">${englishDisclaimer.title}:</b>
          ${englishDisclaimer.text}
        </p>
      </div>
    `;
        }

        /* -------- CREATE PDF ELEMENT -------- */

        const element = document.createElement("div");
        element.style.width = "100%";
        element.style.maxWidth = "794px";

        element.innerHTML = `

<link href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;500&family=Noto+Sans+Devanagari:wght@400;500&family=Noto+Sans+Ol+Chiki:wght@400;500&display=swap" rel="stylesheet">

<style>

.pdf-root{
  font-family:
    'Noto Sans',
    'Noto Sans Devanagari',
    'Noto Sans Ol Chiki',
    Arial,
    sans-serif;
  line-height:1.7;
  font-size : 19px;
}

.pdf-root p,
.pdf-root li{
  line-height:1.7;
  word-break:normal;
  overflow-wrap:anywhere;
  white-space:normal;
  margin-bottom:6px;
  page-break-inside:avoid;
  break-inside:avoid;
}

.pdf-root ul,
.pdf-root ol{
  padding-left:18px;
}

.pdf-root h1,
.pdf-root h2,
.pdf-root h3,
.pdf-root h4{
  page-break-after:avoid;
}

.pdf-root img{
  max-width:100%;
  page-break-inside:avoid;
}

.pdf-block{
  page-break-inside:avoid;
  break-inside:avoid;
}

.minutes-content{
  line-height:1.7;
}

.disclaimer{
  margin-top: 8px;
}

</style>

<div class="pdf-root">

<!-- HEADER -->

<div class="pdf-block" style="display:flex;align-items:center;gap:10px;margin-bottom:10px;">
  <img src="${PanchayatiRajLogo}" style="height:50px;" />

  ${state_name_english ? `<div><b>State:</b> ${state_name_english}</div>` : ""}

  ${zpname ? `<div><b>ZP:</b> ${zpname}${zpcode ? ` (${zpcode})` : ""}</div>` : ""}

  ${bpname ? `<div><b>BP:</b> ${bpname}${bpcode ? ` (${bpcode})` : ""}</div>` : ""}

  ${gpname ? `<div><b>GP:</b> ${gpname}${gpcode ? ` (${gpcode})` : ""}</div>` : ""}
</div>

<hr/>

<!-- MEETING INFO -->

<div class="pdf-block" style="margin-bottom:10px;">
  <div><b>Meeting Type:</b> ${meetingType}</div>
  <div><b>Meeting Title:</b> ${meetingTitle}</div>
</div>

<!-- MINUTES -->

<div class="minutes-content">
  ${htmlContent}
</div>

<!-- DISCLAIMER -->

${disclaimerHTML}

</div>
`;

        const opt: any = {
            margin: [15, 10, 15, 10],
            filename: fileName,

            image: {
                type: "jpeg",
                quality: 0.98,
            },

            html2canvas: {
                scale: 2,
                useCORS: true,
                letterRendering: true,
                scrollY: 0,
                windowWidth: 1200
            },

            jsPDF: {
                unit: "mm",
                format: "a4",
                orientation: "portrait",
            },

            pagebreak: {
                mode: ["css", "legacy"]
            },
        };

        html2pdf()
            .set(opt)
            .from(element)
            .save()
            .then(() => toast.success("Exported as PDF"))
            .catch((err: any) => {
                console.error(err);
                toast.error("Failed to export PDF.");
            });
    };






    const handleSubmit = async () => {
        try {
            setIsEditing(false);
            const payload = {
                task_id: task_id,
                target_language: language,
                edited_minutes: htmlSummaryReg,
            };
            const res = await protectedAPI.post(
                `${baseUrl}api/submit-edited-summary`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );
            const data = res.data;
            if (data?.msg_code === 200) {
                toast.success(data.msg || "Changes submitted successfully");
            } else {
                toast.error(data?.msg || "Something went wrong while submitting.");
                setIsEditing(true);
            }
        } catch (error: any) {
            console.error("Error submitting edited summary:", error);
            toast.error(
                error?.response?.data?.msg ||
                "Failed to submit changes. Please try again."
            );
            setIsEditing(true);
        }
    };


    return (
        <div className="">
            <div
                className="content-output-container for-summary mt-3"
                onClick={() => setIsOpen(!isOpen)}
                style={{ cursor: 'pointer' }}
            >
                <div className="content-output-text">
                    <div className="first-final-icon">
                        <FaListAlt />
                    </div>
                    <p className="first-final-text">View Minutes of the Meeting ({selectedLan})</p>
                </div>
                <div className="icons-actions">

                    <div
                        className="translate-wrapper"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="icon-with-label translate-icon-group">
                            <FaDownload />
                            <span className="icon-label">Export</span>
                            <FaCaretDown className="down-arrow" />
                        </div>

                        <div className="translate-dropdown">
                            <ul>
                                <li
                                    onClick={() =>
                                        handleExportPDF(
                                            htmlSummaryReg,
                                            selectedLan,
                                            "Minutes_of_Meeting.pdf"
                                        )
                                    }
                                >
                                    Export as PDF
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div
                        className={`icon-with-label`}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsEditing(true);
                        }}
                    >
                        <FaPencilAlt />
                        <span className="icon-label">Edit</span>
                    </div>
                    <div
                        className="icon-with-label"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(htmlSummaryReg);
                        }}
                    >
                        <FaRegCopy />
                        <span className="icon-label">Copy</span>
                    </div>


                  
                    <FaCaretDown
                        style={{
                            cursor: 'pointer',
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s ease',
                        }}
                    />
                </div>
            </div>
            <div style={{ display: "none" }}>
                <div ref={printRef} id="print-container"></div>
            </div>

            {isOpen && (
                <>
                    {!isEditing && (
                        <div className="result-content">
                            <div dangerouslySetInnerHTML={{ __html: htmlSummaryReg }} />
                        </div>
                    )}

                    {isEditing && (
                        <div className="dds">
                            <CKEditor
                                editor={ClassicEditor as any}
                                data={htmlSummaryReg || "<p>Start typing...</p>"}
                                onChange={(event, editor) => {
                                    const data = editor.getData();
                                    setHtmlSummaryReg(data);
                                }}
                            />

                            <div className="my-3 d-flex justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={handleSubmit}
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default TranslateSummary;
